import React, {useState, useEffect, useRef, useContext, memo} from 'react';
import {Box, CircularProgress, Typography} from '@mui/material';
import {colorGray} from "../App/theme.js";
import jwtDecode from "jwt-decode";
import {getKBImage} from "../KB/KBImage.js";
import {isMobile, parseJSON, publicChatData} from "../App/utils.js";
import {MESSAGE} from "../App/constants.js";
import {AlertContext} from "../App/App.js";
import {contentRender} from "./ChatContent.js";
import {CircularLoaderWithText} from "../Loaders/CircularLoaderWithText.js";
import {createHandleMessageAddReaction} from "./ChatMessagesHelpers";
import {chatMessageAddReaction, getChatMessages} from "../API/chatAPI";
import Dialog from "@mui/material/Dialog";
import {EmojiPicker} from "./EmojiPicker";
import {useEmojiInitialization} from "./useEmojiInitialization";
import {ChatMessage} from "./ChatMessage";
import {blockAutoscroll} from "./utils";
import {ContextItems} from "./ContextItems";
import {useInputValue} from "../Context/ChatInputProvider";
import {kbUserData} from "../utils";

let rect;
export const ChatMessages = memo(({ contentRenderLoaded,
                                 messages, setWideScroll, setMessages, KBId, imageFiles, chatContainerRef,
                                 RequestChatAPI, isStreaming, KB, sharedChat, chatId, typing, setLastEvaluatedKey,
                                 lastEvaluatedKey, isImageEditor, contextItems, setContextItems, contextWindow,
                                 setContextWindow, hasIndexedDBItems, isContextItemsOpen, setIsContextItemsOpen,
                                 totalTokensSum, setTotalTokensSum, renderSettings, setBlockingLoading, blockingLoading,
                                 sendButtonRef, sendButtonRippleRef
    }) => {
    const {setSystemAlert} = useContext(AlertContext);
    const [windowWidth, setWindowWidth] = useState(window.innerHeight);
    const [loading, setLoading] = useState(false);
    const [ogImage, setOgImage] = useState(null);
    const [shareDetails, setShareDetails] = useState(null);
    const [session, setSession] = useState(null);
    const [activeEmojiMenu, setActiveEmojiMenu] = useState(null);
    const [processedMessages, setProcessedMessages] = useState([]);
    const inputProvider = useInputValue();
    const setInputValue = inputProvider?.setInputValue;
    const iframeRef = useRef(null);
    const boxRef = useRef(null);
    const loaderRef = useRef(null);
    const observer = useRef(null);

    useEffect(() => {
        if (boxRef.current) {
            rect = boxRef.current.getBoundingClientRect();
        }
    }, [messages]);

    // #resize4
    useEffect(() => {
        const hasImageEditorTool = !!KB?.inputTools?.find(o => parseJSON(o)?.type === 'imageEditor');

        const first = !!messages?.find(
            msg => parseJSON(msg.content)?.type === MESSAGE.IMAGE_EDITOR && parseJSON(msg.content)?.first
        );

        const hasImageEditorInMessages = !!messages?.find(msg => parseJSON(msg.content)?.type === MESSAGE.IMAGE_EDITOR);

        if (hasImageEditorTool || sharedChat && hasImageEditorInMessages) {
            if (!first) {
                setMessages(prevMessages => {
                    return [{
                        role: 'user',
                        content: JSON.stringify({
                            type: MESSAGE.IMAGE_EDITOR,
                            first: true,
                            mode: 'Generate'
                        })},
                        ...prevMessages]
                });
            }
        }

        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [KB, messages]);


    // #resize3
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        (async () => {
            const processed = await Promise.all(
                messages.map(async (message, msgIndex) => ({
                    ...message,
                    content: await contentRender({
                        contentRenderLoaded,
                        msgIndex, setWideScroll, messages, setMessages, iframeRef, KB, chatContainerRef,
                        RequestChatAPI, textDetectionsImageFiles: imageFiles, setSystemAlert, sharedChat,
                        setBlockingLoading, blockingLoading, setInputValue, kbUserData, sendButtonRef,
                        sendButtonRippleRef, renderSettings, isStreaming
                    }),
                    _content: message.content
                }))
            );

            setProcessedMessages(processed);
        })();
    }, [messages, iframeRef, KBId, KB, contentRenderLoaded]);

    useEffect(() => {
        if (!sharedChat && !publicChatData ) {
            try {
                const kbToken = localStorage.getItem('kbJWT');
                setSession(jwtDecode(kbToken));
            } catch(e) {
                //@Todo refresh token
                window.dispatchEvent(new CustomEvent('logout'));
            }
        }
    }, [messages]);

    useEffect(() => {
        observer.current = new IntersectionObserver(entries => {
            const first = entries[0];
            if (first.isIntersecting) {
                loadMoreMessages();
            }
        }, { threshold: 1 });

        const currentLoader = loaderRef.current;
        if (currentLoader) {
            observer.current.observe(currentLoader);
        }

        return () => {
            if (currentLoader) {
                observer.current.unobserve(currentLoader);
            }
        };
    }, [loaderRef.current, messages, lastEvaluatedKey, chatContainerRef]);

    const loadMoreMessages = async () => {
        const hasScrolled = chatContainerRef?.current?.hasScrolled > 2;
        if (!hasScrolled || loading || !lastEvaluatedKey) return;
        chatContainerRef.current.hasScrolled = 0;
        document.getElementById('infinite-loader').hidden = true;
        blockAutoscroll();
        setLoading(true);

        // if (chatContainerRef.current) chatContainerRef.current.scrollTop += 50;

        let limit = 25;
        getChatMessages({ chatId, lastEvaluatedKey, limit}).then(data => {
            if (data?.data?.messages) {
                setTimeout(() => document?.getElementById(`message-${limit}`)?.scrollIntoView(), 10)
                setLastEvaluatedKey(data?.data?.lastEvaluatedKey)
                setMessages((prev) => [...data.data.messages, ...prev]);
                document.getElementById('infinite-loader').hidden = false;
            }
            setLoading(false);
        })
    };

    useEmojiInitialization()

    let userPicture;
    userPicture = session?.userPicture;
    const avatars = { user: userPicture, assistant: KBId && getKBImage(KBId)}

    if (typeof userPicture !== 'string' && typeof sharedChat?.avatars?.user !== 'string' && !publicChatData) return null;
    const userPictureURL = sharedChat?.avatars?.user || userPicture;
    const messagesToRender = [...processedMessages, typing];

    // const a = +new Date();

    const renderMessages = () => {
        return messagesToRender
            .map((msg, index) => {

            if (msg?.content === JSON.stringify({ type: 'HIDDEN_MESSAGE' })) {
                return null;
            }

            return <div key={index} id={`message-${index}`}>
                <ChatMessage
                    key={index} {...{
                        index, messages, rect, messagesToRender, isStreaming, boxRef, setMessages, chatId,
                        userPictureURL, sharedChat, KBId, setActiveEmojiMenu, avatars, setLoading, setOgImage,
                        setShareDetails, shareDetails, ogImage, loaderRef, KB, renderSettings, contentRenderLoaded,
                        setBlockingLoading
                    }} />
            </div>
        })
    }

    const debug = false;
    const handleMessageAddReaction = createHandleMessageAddReaction({activeEmojiMenu, chatMessageAddReaction, chatId, setMessages, setActiveEmojiMenu});

    return (
        <Box>
            <CircularLoaderWithText isOpen={loading?.length} key={'CircularLoaderWithText'} message={loading}/>
            {/*{!isMobile && !isImageEditor && <Box style={{position:'absolute', marginTop: '-30px'}}>*/}
            {/*    {!renderSettings?.disableTotalMessagesLabel && <Typography color={colorGray}  fontSize={14}>{messages.length}{lastEvaluatedKey ? ' messages loaded' : ' messages total'}</Typography>}*/}
            {/*    /!*<ContextualData />*!/*/}
            {/*</Box>}*/}

            {!isImageEditor && hasIndexedDBItems && !renderSettings?.disableContextItems && <Box style={{position:'absolute', marginTop: isMobile ? '-8px' : '-30px', zIndex: 100}}>
                <ContextItems {...{contextItems, setContextItems, contextWindow, setContextWindow, messages,
                    KB, isContextItemsOpen, setIsContextItemsOpen, totalTokensSum, setTotalTokensSum}} />
            </Box>}

            {loading === true && <CircularProgress size={24} />}
            {<div ref={loaderRef} id={'infinite-loader'}></div>}

            {renderMessages()}
            {/*{debug && <div style={{position:'absolute', top:0, left: 0, zIndex: 999999}}>{JSON.stringify(times)} length:{length}</div>}*/}
            <Dialog
                PaperProps={{ style: { borderRadius: 10 } }}
                open={!!(activeEmojiMenu)}
                onClose={() => {
                    setActiveEmojiMenu(null)
                }}
                aria-labelledby="emoji-picker-dialog"
                aria-describedby="emoji-picker-dialog-description"
            >
                <EmojiPicker onEmojiSelect={handleMessageAddReaction} />
            </Dialog>
        </Box>
    );
});
